/**
 * The Codex: the forge collection log (crafting.md §6.3). Entry ids are stable save data:
 *   craft:<slot>:<rarity>   each slot × rarity crafted (8 × 5 = 40)
 *   tier:<classId>:<slot>   each tier piece per class (4 × 5 = 20)
 *   leg:<legendaryId>       each legendary (8)
 *   awaken:<kind>           each Awakening type (4)
 *   gild:<slot>             a slot taken to Gilded 5 (8)
 * Every CODEX_LUCK_EVERY entries give +CODEX_LUCK_STEP permanent craft Luck (cap CODEX_LUCK_MAX)
 * and a cosmetic title. Pure.
 */
import type { ClassId } from '@keystone/sim';
import { LEGENDARIES } from './items.legendaries';
import { AWAKEN_TABLE, CODEX_LUCK_EVERY, CODEX_LUCK_MAX, CODEX_LUCK_STEP, GILD_MAX } from './forge.constants';
import type { AwakeningKind } from './forge.constants';
import { TIER_SETS } from './data/tiersets';
import type { ForgeState, Item, ItemSlot, Rarity } from './types';
import { ITEM_SLOTS, RARITIES, TIER_SLOTS } from './types';

/** Cosmetic season id: the Codex's cosmetic track is per season; Luck and pity carry over. */
export const SEASON_ID = 'season-1';

export const codexCraftId = (slot: ItemSlot, rarity: Rarity): string => `craft:${slot}:${rarity}`;
export const codexTierId = (classId: ClassId, slot: ItemSlot): string => `tier:${classId}:${slot}`;
export const codexLegendaryId = (legendaryId: string): string => `leg:${legendaryId}`;
export const codexAwakenId = (kind: AwakeningKind): string => `awaken:${kind}`;
export const codexGildId = (slot: ItemSlot): string => `gild:${slot}`;

/** Every Codex entry id, grouped (the Codex screen's pages). */
export function codexCatalog(): { group: 'craft' | 'tier' | 'legendary' | 'awaken' | 'gild'; ids: string[] }[] {
  return [
    { group: 'craft', ids: ITEM_SLOTS.flatMap((s) => RARITIES.map((r) => codexCraftId(s, r))) },
    { group: 'tier', ids: TIER_SETS.flatMap((set) => TIER_SLOTS.map((s) => codexTierId(set.classId, s))) },
    { group: 'legendary', ids: LEGENDARIES.map((l) => codexLegendaryId(l.id)) },
    { group: 'awaken', ids: AWAKEN_TABLE.map((a) => codexAwakenId(a.kind)) },
    { group: 'gild', ids: ITEM_SLOTS.map((s) => codexGildId(s)) },
  ];
}

export const CODEX_SIZE = (): number => codexCatalog().reduce((n, g) => n + g.ids.length, 0);

/** Permanent craft Luck from Codex entries. */
export function codexLuck(codex: readonly string[]): number {
  return Math.min(CODEX_LUCK_MAX, Math.floor(codex.length / CODEX_LUCK_EVERY) * CODEX_LUCK_STEP);
}

/** Titles unlocked at entry counts (cosmetic). */
export const CODEX_TITLES: readonly { at: number; title: string }[] = [
  { at: 10, title: 'Altar-Touched' },
  { at: 20, title: 'Scaleborn' },
  { at: 30, title: 'Coilforged' },
  { at: 40, title: 'Eye of the Serpent' },
  { at: 60, title: 'Keeper of the Coil' },
  { at: 80, title: 'The Great Coil' },
];

export function codexTitles(codex: readonly string[]): string[] {
  return CODEX_TITLES.filter((t) => codex.length >= t.at).map((t) => t.title);
}

export interface CodexProgress {
  entries: number;
  total: number;
  luck: number;
  /** Entries until the next +Luck step (null at the cap). */
  nextLuckIn: number | null;
  titles: string[];
  /** Classes whose full 5-piece set is in the Codex (set aura cosmetic). */
  fullSets: ClassId[];
}

export function codexProgress(forge: Pick<ForgeState, 'codex'>): CodexProgress {
  const n = forge.codex.length;
  const luck = codexLuck(forge.codex);
  const fullSets = TIER_SETS.filter((s) => TIER_SLOTS.every((slot) => forge.codex.includes(codexTierId(s.classId, slot)))).map((s) => s.classId);
  return {
    entries: n,
    total: CODEX_SIZE(),
    luck,
    nextLuckIn: luck >= CODEX_LUCK_MAX ? null : CODEX_LUCK_EVERY - (n % CODEX_LUCK_EVERY),
    titles: codexTitles(forge.codex),
    fullSets,
  };
}

/** Add entries (sorted, unique) to a mutable forge state; returns the ids that were new. */
export function addCodex(forge: ForgeState, ids: readonly string[]): string[] {
  const fresh = ids.filter((id) => !forge.codex.includes(id));
  if (fresh.length) forge.codex = [...new Set([...forge.codex, ...fresh])].sort();
  return fresh;
}

/** Codex ids an item earns (`craft` = it was crafted, so its slot × rarity counts). */
export function codexIdsForItem(item: Item, crafted = true): string[] {
  const ids = crafted ? [codexCraftId(item.slot, item.rarity)] : [];
  const set = item.setId ? TIER_SETS.find((s) => s.id === item.setId) : undefined;
  if (set) ids.push(codexTierId(set.classId, item.slot));
  if (item.legendaryId) ids.push(codexLegendaryId(item.legendaryId));
  if ((item.forge?.gilding ?? 0) >= GILD_MAX) ids.push(codexGildId(item.slot));
  return ids;
}
